import { sharedStringsEs } from "../shared";
import type { DeepStringLike } from "../types";
import type { mobileStringsEn } from "./english";

export const mobileStringsEs = {
  /**
   *
   * Contractor Mobile App
   *
   */
  ui: {
    back: "Atrás",
    confirm: "Confirmar",
    continue: "Continuar",
    required: "Requerido",
    add_more: "Agregar más",
    offline_indicator: {
      offline: "Sin conexión",
      pending_upload_singular: "1 carga pendiente",
      pending_upload_plural: "{count} cargas pendientes",
    },
    error: {
      something_went_wrong: "Algo salió mal",
      error_msg: "La aplicación encontró un error. Nuestro equipo ha sido notificado.",
      refresh: "Actualizar",
    },
  },
  auth: {
    error_messages: {
      ...sharedStringsEs.auth.error_messages,
      // Overrides:
      form_identifier_not_found: "Cuenta no encontrada. Comunícate con tu administrador de oficina.",
      form_identifier_exists: "Ese correo electrónico ya está en uso",
      verification_failed: "Verificación fallida.",
      too_many_requests: "Demasiados intentos fallidos.",
    },
    phone_errors: {
      form_conditional_param_missing: "Número de teléfono inválido",
      form_param_format_invalid: "Número de teléfono inválido",
      form_identifier_exists: "Número de teléfono ya está en uso",
      verification_not_sent: "Mensaje de verificación no enviado",
    },
    actions: {
      log_in: "Iniciar sesión",
      back: "Atrás",
      log_out: "Cerrar sesión",
    },
    alerts: {
      code_sent: "Hemos enviado un código a",
      code_resent: "Hemos enviado otro código a",
    },
    prompts: {
      phone_number: "Número de teléfono",
      verification_code: "Ingresa el código de verificación",
      did_not_get_text: "¿No recibiste el mensaje?",
      send_again: "Enviar de nuevo",
      not_a_contractor: "¿No eres un contratista de Remi?",
      become_one_here: "Conviértete en uno aquí",
    },
    agent_sign_in: {
      signing_in: "Iniciando sesión…",
      failed: "No se pudo iniciar sesión con este enlace. Genera uno nuevo e inténtalo de nuevo.",
    },
    logout_modal: {
      title: "¿Cerrar sesión?",
      description: "Serás desconectado y necesitarás ingresar tu información de inicio de sesión para volver a entrar.",
      cancel_button: "No, gracias",
      confirm_button: "Cerrar sesión",
      loading_text: "Cerrando sesión...",
      error_message: "Error al cerrar sesión. Por favor, inténtalo de nuevo.",
    },
    delete_account_modal: {
      title: "¿Eliminar cuenta?",
      description:
        "Esto borrará todos los datos personales y preferencias asociados con tu cuenta de acuerdo con nuestra política de privacidad. Esta acción es irreversible.",
      cancel_button: "No, gracias",
      confirm_button: "Eliminar",
      loading_text: "Eliminando...",
      error_message: "Error al eliminar cuenta. Por favor, inténtalo de nuevo.",
    },
    setup_name: {
      welcome: "Bienvenido a Remi",
      description: "Por favor ingresa tu nombre para comenzar. Esto nos ayuda a identificar tu trabajo en el sitio.",
      first_name: "Nombre",
      first_name_placeholder: "Ingresa tu nombre",
      last_name: "Apellido",
      last_name_placeholder: "Ingresa tu apellido",
      required_alert: "Por favor ingresa tu nombre y apellido.",
      error_alert: "Error al guardar tu nombre. Por favor, inténtalo de nuevo.",
      saving: "Guardando...",
    },
    link_access: {
      verifying: "Verificando acceso...",
      redirecting: "Redirigiendo...",
      title: "No se pudo acceder al enlace",
      invalid_link: "Enlace inválido",
      generic_error: "Error al acceder al enlace",
      permission_denied:
        "Las notificaciones deben estar activadas para abrir este enlace. Actívalas en Configuración e inténtalo de nuevo.",
      push_token_failed: "No pudimos verificar este dispositivo. Revisa tu conexión e inténtalo de nuevo.",
      contact_supervisor: "Por favor contacta a tu supervisor si el problema persiste.",
      retry: "Intentar de nuevo",
    },
    email_label: "Correo electrónico",
    email_placeholder: "nombre@email.com",
    password_label: "Contraseña",
    password_placeholder: "Ingresa tu contraseña",
    use_phone_instead: "Usar número de teléfono",
    forgot_password: "¿Olvidaste tu contraseña?",
    credential: {
      forgot_password_title: "Restablecer contraseña",
      forgot_password_prompt: "Ingresa tu correo para recibir un código de restablecimiento",
      reset_code_prompt: "Ingresa el código enviado a tu correo",
      new_password_label: "Nueva contraseña",
      new_password_placeholder: "Ingresa nueva contraseña",
      reset_password_button: "Restablecer contraseña",
      setup_description:
        "Agrega un correo electrónico y contraseña para proteger tu cuenta con autenticación multifactor.",
      add_email_label: "Correo electrónico",
      add_email_placeholder: "nombre@email.com",
      verify_email_prompt: "Ingresa el código de verificación enviado a tu correo",
      create_password_label: "Crea una contraseña",
      create_password_placeholder: "Ingresa una contraseña",
      setup_complete_title: "Correo electrónico y contraseña agregados",
      password_set: "Contraseña configurada",
      setup_button: "Continuar",
      verify_button: "Verificar",
      save_password_button: "Guardar contraseña",
      use_different_email: "Usar otro correo electrónico",
      password_requirements_title: "Tu contraseña debe:",
      password_requirement_length: "Tener al menos {count} caracteres",
      password_requirement_common: "No ser una palabra común ni un patrón predecible",
      password_requirement_breach: "No aparecer en una filtración de datos conocida",
      enrollment_prompt_title: "Configura tu correo y contraseña ahora",
      enrollment_prompt_description: "Por favor agrega un correo electrónico y una contraseña a tu cuenta",
      enrollment_prompt_button: "Crear inicio de sesión",
      forgot_password_sent: "Si existe una cuenta con ese correo, hemos enviado un código de restablecimiento.",
    },
    mfa_challenge: {
      title: "Verificación en dos pasos",
      totp_subtitle: "Ingresa el código de tu aplicación de autenticación",
      sms_subtitle: "Ingresa el código enviado a {phone}",
      backup_subtitle: "Ingresa uno de tus códigos de respaldo",
      method_totp: "Aplicación de autenticación",
      method_sms: "Mensaje de texto",
      method_backup: "Código de respaldo",
      use_different_method: "Usar otro método",
      verify_button: "Verificar",
      resend_code: "Reenviar código",
      code_resent: "Código reenviado",
      resend_failed: "No pudimos enviar un código. Revisa tu señal e inténtalo de nuevo.",
      same_number_info: "Este código es diferente al que usaste para iniciar sesión",
      back_to_signin: "Volver a iniciar sesión",
      invalid_code: "Código inválido. Inténtalo de nuevo.",
    },
    mfa_required: {
      title: "Verificación en dos pasos requerida",
      enrolled_description:
        "Tu sesión necesita ser verificada. Cierra sesión e inicia sesión de nuevo para completar la verificación en dos pasos.",
      unenrolled_description: "Tu cuenta requiere verificación en dos pasos. Configúrala en la web para continuar.",
      sign_out_button: "Cerrar sesión",
      setup_web_button: "Configurar en la web",
    },
  },
  onboarding: {
    shared: {
      skip: "Omitir",
      open_settings: "Abrir configuración",
    },
    terms_of_service: {
      subtitle: "Razón para el acceso a la cámara y fotos",
      terms_of_service: "Términos de Servicio",
      privacy_policy: "Política de Privacidad",
      title: "Transparencia de privacidad de Remi",
      body: "Para documentar el progreso del proyecto y verificar la finalización del trabajo, le pedimos que permita el acceso a su cámara y biblioteca de fotos.\n\nSolo accedemos a las fotos que usted elige capturar o cargar a través de la aplicación. No accedemos a su cámara ni a su biblioteca de fotos más allá de eso, y no utilizamos sus fotos para reconocimiento facial ni fines de identificación.",
      learn_more: "Más información",
      agree_to_terms: "Al usar Remi, usted acepta nuestros {termsOfService} y {privacyPolicy}",
      terms_acknowledgement: "He leído y acepto los Términos de Servicio y la Política de Privacidad",
      terms_acknowledgement_error: "Debes aceptar los términos de servicio para continuar",
      agree_and_continue: "Aceptar y continuar",
    },
    turn_on_camera: {
      title: "Ya casi estás — solo un paso más",
      description: "Facilita tu trabajo, permite el acceso a la cámara para capturar fotos directamente en Remi.",
      grant_camera_access: "Permitir acceso a la cámara",
    },
    turn_on_notifications: {
      title: "Recibe notificaciones cuando estés registrado",
      description:
        "Te notificaremos cuando estés registrado. Para ahorrar tiempo y facilitar tu trabajo, puedes tocar la alerta para ver los detalles del proyecto.",
      turn_on_notifications: "Sí, notifícame",
      notifications_already_enabled: "¡Las notificaciones ya están activadas! Todo listo.",
      enable_in_settings: "Por favor, activa las notificaciones en la configuración de tu dispositivo",
      notifications_enabled: "¡Las notificaciones ya están activadas! Todo listo.",
    },
    walkthrough: {
      project_checklists: "Listas de verificación del proyecto",
      checklists_msg:
        "Cada proyecto tiene requisitos que deberás completar. Toca cualquier elemento para ver lo que se necesita.",
      requirement_instructions: "Instrucciones del requisito",
      instructions_msg:
        "Cada requisito tiene instrucciones, esto incluye un ejemplo de foto y una explicación detallada de lo que se necesita.",
      completing_checklist: "Completar una lista de verificación",
      completing_msg:
        "Una vez que la lista de verificación esté terminada, haz clic en enviar para notificar al administrador de Remi que está lista para revisión.",
    },
  },
  home: {
    checked_in_jobs: "Trabajos registrados",
    day_picker: {
      one_job: "1 trabajo",
      jobs: "{count} trabajos",
      see_all: "Ver todo",
      previous_month: "Mes anterior",
      next_month: "Mes siguiente",
      today: "hoy",
    },
    empty_state: {
      title: "¡No hay trabajos para mostrar — aún!",
      description: "Una vez que llegues a un sitio de trabajo, tu trabajo aparecerá aquí automáticamente.",
    },
    header: {
      open_schedule: "Abrir agenda",
    },
    no_jobs_for_day: "No hay trabajos programados para este día.",
    route: {
      next: "Siguiente",
      next_stop: "Siguiente · {window}",
      directions: "Cómo llegar",
      details: "Detalles",
      schedule: "Agenda",
      stops_summary_singular: "1 parada · terminadas: {done}",
      stops_summary_plural: "{count} paradas · terminadas: {done}",
      today_label: "Hoy · {date}",
      offline_local: "Sin conexión · Local",
      no_stops_today: "No hay paradas hoy",
      no_stops_on_day: "No hay paradas este día",
      no_stops_offline: "No estás en la ruta. Vuelve a conectarte para ver nuevas paradas de despacho.",
      no_stops_online: "No estás en la ruta. Abre tu agenda para ver lo que sigue.",
      all_done_title: "Todas las paradas terminadas",
      all_done_description: "Todas las paradas de este día están terminadas.",
      stop_count_singular: "1 parada",
      stop_count_plural: "{count} paradas",
      next_day_tomorrow: "Mañana · {stops}",
      next_day_dated: "{date} · {stops}",
      nothing_upcoming: "Nada próximo",
      open_in_google_maps_title: "¿Abrir en “Google Maps”?",
      open_in_google_maps_cancel: "Cancelar",
      open_in_google_maps_open: "Abrir",
      review_needed: "Revisión necesaria · {item}",
      today: "Hoy",
      this_week: "Esta semana",
      distance_miles: "{miles} mi",
      done_chip: "Hecha",
      agenda_subtitle: "{category} · {city}, {state}",
      show_today: "Ver las paradas de hoy",
      hide_today: "Volver a la siguiente parada",
      load_failed_title: "No se pudieron cargar tus paradas",
      load_failed_description: "Revisa tu conexión e inténtalo de nuevo.",
      retry: "Reintentar",
      window_short: {
        AM: "AM",
        PM: "PM",
        EVENING: "Noche",
        ANY: "Libre",
      },
    },
    tabs: {
      home: "Inicio",
      profile: "Perfil",
    },
    schedule: {
      search_placeholder: "Buscar trabajos, direcciones, reclamos",
      week: "Semana",
      month: "Mes",
      today_header: "Hoy · {date}",
      day_header: "{weekday} · {date}",
      no_stops: "Sin paradas",
      no_results: "Ningún trabajo coincide con “{query}”",
    },
    menu: {
      checklist: "Lista de verificación",
      terms_and_conditions: "Términos y condiciones",
      privacy_notice: "Aviso de privacidad",
      prohibited_use_policy: "Política de uso prohibido",
      version: {
        line: "Versión {version} ({build})",
        line_with_environment: "Versión {version} ({build}) · {environment}",
        unknown: "desconocido",
      },
    },
  },
  profile_redesign: {
    credential_title: "Agrega correo y contraseña",
    credential_description: "Inicio solo con teléfono · configúralo para usar cualquier dispositivo",
    credential_action: "Configurar",
    organization: "Organización",
    admin_role: "Administrador",
    preferences: "Preferencias",
    notifications: "Notificaciones",
    notifications_managed_ios: "Se administra en Ajustes de iOS",
    notifications_managed_android: "Se administra en los ajustes del sistema",
    on: "Activadas",
    off: "Desactivadas",
    save_images: "Guardar imágenes en el teléfono",
    save_images_description: "Copia las fotos de la cámara a tu galería",
    location: "Acceso a la ubicación",
    admin_impersonate: "Administrar suplantación",
    admin_stop_impersonating: "Dejar de suplantar a {name}",
    admin_stopping: "Deteniendo…",
    admin_clear_onboarding: "Borrar onboarding",
    admin_clear_link_auth: "Borrar autenticación por enlace",
    admin_invalidate_queries: "Invalidar todas las consultas",
    admin_test_logger: "Probar el registro móvil",
    admin_seed_job: "Crear visita de prueba",
    admin_seed_job_with_checklist: "Crear visita de prueba con lista completa",
    admin_seeding: "Creando…",
    admin_push_token: "Token de notificaciones",
    impersonation_title: "Suplantar a un usuario",
    impersonation_close: "Cerrar",
    impersonation_search: "Buscar por nombre, correo u organización",
    impersonation_hint: "Cuentas de contratistas que pueden iniciar sesión",
    impersonation_error: "No se pudieron cargar los usuarios. Inténtalo de nuevo.",
    impersonation_empty: "Ningún usuario coincide",
    impersonation_select: "Selecciona un usuario",
    impersonation_start: "Suplantar a {name}",
    admin_section: "Administración",
    legal: "Legal",
    footer: "{version} · Sesión iniciada como {identity}",
  },
  job_detail_redesign: {
    done: "Listo",
    subtitle: "{category} · {window} · {city}",
    subtitle_without_window: "{category} · {city}",
    message: "Enviar mensaje a {name}",
    call: "Llamar a {name}",
    copy: "Copiar {field}",
    copied: "Copiado",
    rows: {
      mobile: "Celular",
      email: "Correo",
      roof: "Techo",
      claim: "Reclamo",
      carrier: "Aseguradora",
      scheduled: "Programado",
      scope_notes: "Notas del alcance",
      site_notes: "Notas del sitio",
      checked_in: "Registro de llegada",
      checklist_completed: "Lista terminada",
    },
    roof_squares: "{count} sq",
    roof_age: "{years} años",
    roof_material: {
      ASPHALT: "teja asfáltica",
      ROLLED_COMP: "rollo asfáltico",
      METAL: "metal",
      TILE: "teja",
      TPO: "TPO",
      EPDM: "EPDM",
      SHAKE: "tablilla de madera",
    },
    checklist_title: "Lista de inspección",
    structure_title: "Estructura {number}",
    checklist_progress: "{completed} de {total} preguntas · {status}",
    checklist_not_started: "sin empezar",
    start_inspection: "Iniciar inspección",
    continue_inspection: "Continuar inspección",
    edit_inspection: "Editar inspección",
    on_the_way_title: "¿Vas para allá?",
    work_order_title: "Orden de trabajo: {category}",
    work_order_subtitle: "El trabajo que revisa esta visita de QA",
  },
  checklist_redesign: {
    back_to_job: "Trabajo",
    optional: "Opcional",
    photos_required: "Requerida · {n} de {required} fotos",
    photos_optional: "Opcional · {n} de {max} fotos",
    photos_required_over: "Requerida · {n} fotos",
    photos_optional_count_one: "Opcional · 1 foto",
    photos_optional_count_other: "Opcional · {n} fotos",
    pick_one_required: "Requerida · elige una",
    yes: "Sí",
    no: "No",
    next_section: "Siguiente · {section}",
    submit_inspection: "Enviar inspección",
    submit_qa: "Enviar QA",
    open_sections: "Abrir secciones",
    edit_checklist: "Editar lista",
    confirm_value: "Confirmar {value}",
    use_this_date: "Usar esta fecha",
    date_today: "Hoy",
    date_confirmed: "Confirmada",
    date_offset_one: "{sign}1 día",
    date_offset_other: "{sign}{n} días",
    decrease: "Disminuir",
    increase: "Aumentar",
    previous_day: "Día anterior",
    next_day: "Día siguiente",
    type_or_dictate: "Escribe o dicta",
    dictate: "Dictar",
    save_note: "Guardar nota",
    update_note: "Actualizar nota",
    take_photo: "Tomar foto",
    take_photo_number: "Tomar foto {n} de {required}",
    add_another_photo: "Agregar otra",
    take_video: "Grabar video",
    take_another_video: "Grabar otro video",
    upload_from_library: "Biblioteca",
    open_photo: "Abrir foto {n}",
    open_video: "Abrir video {n}",
    open_all_photos: "Abrir las {n} fotos",
    photo_grid_count_one: "1 foto",
    photo_grid_count_other: "{n} fotos",
    photo_grid_uploading: "{count} · {n} subiendo",
    photo_grid_hint: "Más recientes primero · toca para ver · mantén para quitar",
    photo_grid_done: "Listo",
    photo_uploading: "Subiendo",
    remove_photo_title: "¿Quitar esta foto?",
    remove_photo_confirm: "Quitar",
    remove_photo_cancel: "Cancelar",
    remove_photo_below_required: "Este elemento necesita {required} fotos. Si quitas esta, quedan {n}.",
    viewer_close: "Cerrar",
    viewer_counter: "{n} de {total}",
    viewer_download: "Guardar en Fotos",
    viewer_remove: "Quitar foto",
    group_done: "Listo",
    group_progress: "{done} de {total}",
    submit: "Enviar",
    sections_label: "Secciones",
    sheet_eyebrow: "{address} · {kind}",
    sheet_kind_inspection: "Inspección",
    sheet_kind_qa: "Visita de QA",
    submit_title_inspection: "¿Enviar inspección?",
    submit_title_qa: "¿Enviar QA?",
    submit_title_open_one: "¿Enviar con 1 elemento pendiente?",
    submit_title_open_other: "¿Enviar con {n} elementos pendientes?",
    submit_subtitle: "{done} de {total} respondidas · la oficina recibe el informe ahora",
    submit_incomplete_subtitle: "La oficina ve lo que falta y puede devolverla para completarla.",
    submit_as_incomplete: "Enviar como incompleta",
    review_needed: "Revisión necesaria",
    review_card_one: "Repetir 1 elemento · {section}",
    review_card_other: "Repetir {n} elementos · {section}",
    retake_requested: "Repetición solicitada",
    retake_photo: "Repetir foto",
    retake_video: "Repetir video",
    structures_title: "Estructuras",
    structures_intro: "Cada estructura asegurada recibe la inspección completa. Toca una para trabajar en ella.",
    structure_complete: "Completa",
    structure_in_progress: "En curso",
    structure_not_started_one: "Sin empezar · 1 pregunta",
    structure_not_started_other: "Sin empezar · {total} preguntas",
    structure_resume: "Continuar",
    add_structure: "+ Agregar una estructura",
    from_the_claim: "Del reclamo",
    from_the_claim_meta: "Del reclamo · confirma o edita",
  },
  upload_activity: {
    uploading_title: "Subiendo fotos",
    waiting_title: "Esperando para subir",
    synced_title: "Fotos sincronizadas",
    progress: "{done} de {total} fotos",
    waiting: "Esperando · {n} guardadas",
    synced: "Sincronizado",
  },
  project: {
    loading: "Cargando...",
    not_found: "Proyecto no encontrado",
    not_found_msg: "El proyecto que buscas no existe o no tienes acceso.",
    view_all_projects: "Ver todos los proyectos",
    checked_in: "Registrado",
    scope_of_work_description: "Descripción del alcance de trabajo",
    scope_of_work: "Alcance de trabajo",
    ho_info: "Info del propietario",
    homeowner_info: "Información del propietario",
    phone_withheld: "Número de teléfono no disponible",
    key_dates: "Fechas clave",
    details: "Detalles",
    files: "Archivos",
    photos: "Fotos",
    tabs: {
      overview: "Resumen",
      checklist: "Lista de verificación",
      photos: "Fotos",
    },
    financials: "Finanzas",
    my_tasks: "Mis tareas",
    all: "Todos",
    current: "Actual",
    completed: "Completado",
    general: "General",
    project_properties: "Propiedades del proyecto",
    project_tasks: "Tareas del proyecto",
    copy_project_link: "Copiar enlace del proyecto",
    generating_link: "Generando enlace...",
    checklist_complete: "Lista de verificación completada",
    unable_edit_checklist: "No se puede editar la lista de verificación",
    edit_checklist_unavailable_offline:
      "Esta lista todavía no está guardada en este dispositivo. Conéctate a internet para editarla.",
    edit_checklist_sync_failed:
      "No se pudo reabrir una lista que editaste sin conexión, probablemente porque la oficina ya la revisó. Tus cambios se guardan en este dispositivo. Avisa a la oficina.",
    opening: "Abriendo\u2026",
    edit: "Editar",
    continue_checklist: "Continuar lista de fotos",
    begin_checklist: "Iniciar lista de fotos",
    copied: "Copiado",
    link_copied: "Enlace del proyecto copiado al portapapeles",
    link_error: "Error al generar enlace del proyecto",
    record_install_started: "Registrar inicio de instalación",
    create_upload_work_order: "Crear y subir orden de trabajo",
    get_ahj_approval: "Obtener aprobación de AHJ",
    change_order_status: "Orden de cambio aprobada/rechazada por EPC",
    validate_photos: "Validar que las fotos del trabajo completado sean aceptables",
    eagleview: {
      title: "EagleView",
      description: "Vista general del diseño y medidas del techo",
    },
    work_order: {
      title: "Orden de trabajo: {category}",
      description: "El trabajo que revisa esta visita de QA",
      unavailable: "No se pudo abrir la orden de trabajo. Inténtalo de nuevo.",
    },
    roof_image: {
      title: "Imagen del techo",
      description: "Guía visual clara del área del techo",
      unavailable: "Imagen del techo no disponible",
    },
    job_details: {
      title: "Detalles del trabajo",
      job_type: "Tipo de trabajo",
      square_count: "Cantidad de pies cuadrados",
      square_count_singular: "pie cuadrado",
      square_count_plural: "pies cuadrados",
      stop: "Parada",
      site_visit_window: {
        AM: "Mañana (7–12)",
        PM: "Tarde (12–5)",
        EVENING: "Noche (5–9)",
        ANY: "Flexible",
      },
      permit_required: "Permiso requerido",
      final_inspection_required: "Inspección final requerida",
      ahj_requirements: "Requisitos de la ciudad",
      hover_link: "Ver en Hover",
      sow_notes: "Notas de alcance",
      unattended_visit: "Visita desatendida",
      checklist_completed: "Lista completada el",
    },
    additional_information: {
      title: "Información adicional del propietario",
    },
    job_context: {
      button: "Contexto del trabajo",
      title: "Contexto del trabajo",
      job_type: "Tipo de trabajo",
      homeowner_title: "Propietario",
      escalation_label: "Escalación",
      escalation_active: "Escalación abierta",
      escalation_previously: "Escalado anteriormente",
      escalation_none: "Sin escalaciones",
      risk_label: "Riesgo de la relación",
      risk_scored_at: "Riesgo de la relación · evaluado el {date}",
      risk_unscored: "Aún sin evaluar",
      risk_tiers: {
        HEALTHY: "Buena",
        WATCH: "En observación",
        AT_RISK: "En riesgo",
        CRITICAL: "Crítica",
      },
      work_title: "Trabajos realizados en esta casa",
      work_completed: "Terminado el {date}",
      work_contractor_unknown: "Contratista no registrado",
      work_empty: "No hay trabajos terminados registrados para esta casa.",
      load_failed: "No se pudo cargar el contexto del trabajo. Revisa tu conexión e inténtalo de nuevo.",
      retry: "Reintentar",
    },
    escalation: {
      active_title: "Propietario escalado",
      active_body: "Este propietario tiene una escalación abierta. Prepárate antes de tocar la puerta.",
      previously_title: "Propietario escalado anteriormente",
      previously_body: "Este propietario fue escalado en el pasado. Acércate con cuidado.",
    },
    on_the_way: {
      button: "Voy en camino",
      confirm_title: "¿Vas a este trabajo?",
      confirm_message: "Le enviaremos un mensaje al propietario avisando que vas en camino.",
      cancel: "Cancelar",
      confirm: "Enviar",
      notified: "Se avisó al propietario que vas en camino",
      error: "No pudimos avisar al propietario. Inténtalo de nuevo.",
      another_en_route: "Termina tu visita actual antes de ir a la siguiente.",
    },
    cant_make_it: {
      button: "No puedo ir",
      confirm_title: "¿No puedes ir a esta visita?",
      confirm_message:
        "La quitaremos de tu ruta y le buscaremos un nuevo horario al propietario. Si ya te está esperando, le enviaremos un mensaje sobre el cambio.",
      cancel: "Mantenerla",
      confirm: "No puedo ir",
      done_title: "Quitada de tu ruta",
      done_rebooked: "Ya le reservamos un nuevo horario al propietario.",
      done_unbooked: "Despacho le buscará un nuevo horario al propietario.",
      ok: "OK",
      error: "No pudimos quitar esta visita. Inténtalo de nuevo.",
    },
    check_in: {
      title: "Registro",
      checked_in_message: "Te has registrado para este trabajo:",
      checked_in_time: "Registrado a las",
      undo_check_in: "Eliminar registro",
      not_on_site: "¿No estás en el sitio?",
      view_project: "Ver proyecto",
      undo_check_in_modal: {
        title: "¿No estás en el sitio?",
        description: "Parece que te has registrado por error. Si deshaces esto, se eliminará tu estado de registro.",
        cancel: "Cancelar",
        confirm: "Deshacer registro",
        error: "No se pudo deshacer el registro. Por favor, inténtalo de nuevo.",
      },
      multiple_worksites_title:
        "Parece que estás cerca de más de un trabajo. Selecciona el sitio de trabajo en el que estás actualmente:",
      safety_acknowledgement_text:
        "Reconozco que yo y todas las demás personas en el sitio tenemos acceso a la información de emergencia y a los baños, y que comprendemos las zonas peligrosas del sitio de trabajo",
    },
    checklist: {
      another_visit_active: "Termina tu visita actual antes de empezar esta.",
      not_found: "Lista de verificación no encontrada",
      not_found_msg: "Esta lista de verificación no existe o no tienes acceso a ella.",
      submit: "Enviar",
      submit_incomplete_title: "¿Enviar lista incompleta?",
      submit_incomplete_message:
        "Aún faltan algunos elementos obligatorios. Una vez que envíes, no podrás agregar más respuestas.",
      submit_incomplete_confirm: "Enviar",
      submit_incomplete_cancel: "Cancelar",
      submit_empty_title: "No se puede enviar una lista vacía",
      submit_empty_message: "Responde al menos una pregunta antes de enviar.",
      syncing: "Sincronizando…",
      submitting: "Enviando…",
      complete_title: "Lista de verificación completa",
      approved_title: "Lista de verificación aprobada",
      submitted_title: "¡Lista de verificación enviada!",
      submitted_description:
        "Gracias por completar la lista de verificación, tu administrador de Remi ha sido notificado.",
      back_to_project: "Volver al proyecto",
      back_to_home: "Volver al inicio",
      site_walk: "Recorrido del sitio",
      photo_house_number: "Foto del número de casa",
      photo_required: "{n} foto requerida",
      n_required: "{n} requerido(s)",
      signature: "Firma",
      signature_required: "{n} firma requerida",
      sign: "Firmar",
      signature_field: "Campo de firma",
      selfie_house_number: "Selfie con número de casa",
      selfie_instructions:
        "Tómate una selfie frente al número de casa como evidencia de que estás comenzando el trabajo en la casa correcta.",
      ladder_location: "Ubicación de la escalera",
      ladder_location_photo: "Foto de ubicación de la escalera",
      preexisting_property_damage: "Imágenes de daños preexistentes en la propiedad",
      preexisting_issues_property: "Imágenes de problemas preexistentes en la propiedad",
      preexisting_equipment: "¿Hay algún problema preexistente con el equipo?",
      preexisting_property: "¿Hay algún problema preexistente en la propiedad?",
      noticeable_damage: "¿Hay algún daño visible que haya ocurrido antes de tu llegada a la propiedad?",
      yes: "Sí",
      no: "No",
      photos_required: "{n}/{total} fotos requeridas",
      photos_optional: "Opcional",
      photos_uploading: "Subiendo…",
      photo_waiting_to_upload: "Foto tomada, esperando para subir",
      photos_waiting_to_upload: "{count} fotos tomadas, esperando para subir",
      no_photos_uploaded: "No se han subido fotos",
      step_progress: "{completed} de {total}",
      items_uploading: "{n} elementos subiendo...",
      offline_waiting: "Dispositivo sin conexión. {n} elementos esperando para subir.",
      offline_waiting_alt: "Estás sin conexión. {n} elementos esperando para subir.",
      upload_incomplete_title: "Carga incompleta",
      upload_incomplete_some_photos:
        "Algunas fotos ya no estaban disponibles y no se pudieron guardar. Las fotos restantes se guardaron.",
      upload_incomplete_no_photos:
        "La foto seleccionada ya no estaba disponible y no se pudo guardar. Inténtalo de nuevo.",
      upload_failed_title: "Error al subir",
      upload_failed_message: "No se pudo subir. Inténtalo de nuevo.",
      upload_failed_icloud:
        "Este elemento está guardado en iCloud y no se pudo descargar a tu teléfono. Revisa tu conexión e inténtalo de nuevo.",
      videos_skipped_title: "Algunos videos no se agregaron",
      videos_skipped_too_long:
        "Los videos deben durar {seconds} segundos o menos. Recorta el video e inténtalo de nuevo.",
      videos_skipped_too_large:
        "Los videos deben pesar {megabytes} MB o menos. Elige un clip más corto e inténtalo de nuevo.",
      videos_skipped_unsupported: "Solo se pueden agregar videos MP4, MOV, WebM y 3GP.",
      conditional_questions: "Preguntas condicionales",
      enter_response: "Ingresa tu respuesta",
      enter_a_number: "Ingresa un número",
      select_a_date: "Selecciona una fecha",
      upload_file: "Subir archivo",
      tap_to_upload: "Toca para subir",
      loading: "Cargando…",
      yes_no: "Sí / No",
      select: "Seleccionar",
      select_one: "Selecciona una",
      repeat_item_fallback: "Elemento",
      jump_to_section: "Ir a la sección",
      optional: "Opcional",
      unavailable: "Lista de verificación no disponible.",
      offline_not_loaded_title: "No disponible sin conexión",
      offline_not_loaded_msg:
        "Esta lista de verificación aún no se ha cargado en este dispositivo. Conéctate a internet para abrirla y después funcionará sin conexión.",
      progress: "Progreso",
      changed_by_another_user: "Modificado por otro usuario",
      field_locked_by: "{name} está editando este campo",
      field_locked_by_unknown: "Otro usuario está editando este campo",
      field_uploading_by: "{name} está subiendo…",
      field_uploading_by_unknown: "Alguien está subiendo…",
      visit_checklists: "Listas de verificación de la visita",
      visit_unavailable_msg: "No se pudieron cargar las listas de esta visita, o no tienes acceso a ellas.",
      visit_progress: "{submitted} de {total} enviadas",
      visit_complete: "Inspección enviada",
      visit_in_progress: "{count} en curso",
      open_checklists: "Abrir",
      status_not_started: "Sin empezar",
      status_draft: "En progreso",
      status_submitted: "Enviada",
      status_approved: "Aprobada",
      status_rejected: "Revisión necesaria",
      row_canceled: "Retirado de esta visita",
      uploads_failed: "{count} no se pudieron subir",
      change_checklist_type: "Cambiar",
      change_checklist_type_title: "¿Cuál lista es esta?",
      change_checklist_type_failed_title: "No se cambió la lista",
      change_checklist_type_failed_message:
        "Puede que alguien ya haya comenzado esta lista. Desliza para actualizar y revisa cuál es.",
      change_checklist_type_submitted_message: "Esta lista ya se envió, así que no se puede cambiar su tipo.",
      change_checklist_type_unpublished_message:
        "Esa lista todavía no está disponible para este trabajo. Elige otra o avísale a la oficina.",
      change_checklist_type_unavailable_offline_message:
        "Este tipo de lista todavía no está guardado en este dispositivo. Conéctate a internet para cambiarlo.",
      change_checklist_type_sync_failed_message:
        "No se pudo guardar un tipo de lista que cambiaste sin conexión, así que esa lista conservó su tipo anterior. Avisa a la oficina.",
      change_checklist_type_offline_message: "No se pudo conectar con Remi. Revisa tu conexión e inténtalo de nuevo.",
      change_checklist_type_started_message: "Ya empezaste esta lista, así que no se puede cambiar su tipo.",
      add_structure: "Agregar otra estructura",
      add_structure_confirm_title: "¿Agregar otra estructura?",
      add_structure_confirm_message:
        "Agrega otra lista de {checklist} para otro edificio en esta propiedad, como un garaje separado.",
      add_structure_confirm: "Agregar",
      add_structure_cancel: "Cancelar",
      add_structure_failed_title: "No se agregó la estructura",
      add_structure_failed_message:
        "Desliza para actualizar e inténtalo de nuevo. Si sigue fallando, avisa a la oficina.",
      add_structure_unavailable_offline_message:
        "Esta lista todavía no está guardada en este dispositivo. Conéctate a internet para agregar una estructura.",
      add_structure_sync_failed_message:
        "No se pudo guardar una estructura que agregaste sin conexión. Su lista se guarda en este dispositivo. Avisa a la oficina.",
      structure_label: "Estructura {number}",
      add_structure_submitted_message:
        "Ya se enviaron todas las listas de esta visita, así que no se puede agregar una estructura.",
      switch_checklist: "Cambiar de lista",
      submit_visit: "Enviar inspección",
      verdict_required_title: "Falta el veredicto",
      verdict_required_message: "Indica si este trabajo necesita correcciones antes de enviar.",
      verdict_required_for: "{checklists} todavía necesita un veredicto antes de poder enviar.",
      visit_submitted_title: "Inspección enviada",
      visit_submitted_message: "Se enviaron todas las listas de esta visita.",
      visit_submit_partial_title: "Algunas listas no se enviaron",
      visit_submit_partial_message: "No se enviaron: {checklists}. Abre cada una en la lista y envíala de nuevo.",
      visit_submit_needs_connection_title: "Conéctate para enviar",
      visit_submit_needs_connection_message:
        "Aún no se guardó en este teléfono: {checklists}. Conéctate a internet para enviar la inspección.",
    },
    downloadPhotos: {
      buttonLabel: "Descargar fotos de la lista",
      savingPhotos: "Guardando fotos...",
      successTitle: "Descargado",
      successMessage: "{count} fotos guardadas en tu galería.",
      successMessageSingular: "1 foto guardada en tu galería.",
      partialTitle: "Descarga completa",
      partialMessage: "Se guardaron {success} de {total} fotos. {failed} no se pudieron descargar.",
      failedTitle: "Descarga fallida",
      failedMessage: "No se pudieron guardar las fotos. Por favor, inténtalo de nuevo.",
      permissionRequiredTitle: "Permiso requerido",
      permissionRequiredMessage: "Por favor permite el acceso a tu galería de fotos para guardar archivos.",
      openSettings: "Abrir Configuración",
      cancel: "Cancelar",
    },
    feedback: {
      additional_context: "Se necesita contexto adicional",
      blurry_photos: "Por favor agrega fotos menos borrosas",
      review_needed: "Revisión necesaria",
      photo_added: "{n} foto agregada",
      sections_need_update: "{n} sección necesita fotos actualizadas",
      request: "Solicitud:",
      updated: "Actualizado",
    },
    qa: {
      schedule_dates: "Programar fecha de inicio y fecha estimada de finalización",
      homeowner_confirmed: "Propietario confirmado",
      homeowner_approved_msg: "El propietario ha aprobado la fecha de inicio programada",
      photo_submission_link: "Enlace de envío de fotos",
      reschedule_reason: "Razón de reprogramación",
    },
    card: {
      completed: "Completada",
      add_photos: "Agregar fotos",
      start: "Iniciar",
      on_my_way: "En camino",
      checklists: "Listas",
      address_not_mapped: "Dirección sin ubicar",
      on_site: "En el sitio",
      drive_time_away: "a {time}",
      checklists_progress: "{submitted}/{total} listas",
    },
    file_management: {
      delete_error: "Error al eliminar archivos. Por favor, inténtalo de nuevo.",
      delete_confirm: "¿Eliminar {count} archivo(s)?",
      delete_confirm_msg: "Esto moverá los archivos seleccionados a la basura.",
      cancel: "Cancelar",
      delete: "Eliminar",
      selected: "{n} seleccionado(s)",
      actions: "Acciones",
      download: "Descargar",
      move_to_trash: "Mover a la basura",
      delete_photo: "Eliminar foto",
      delete_photo_confirm: "¿Estás seguro de que quieres mover esta foto a la basura?",
      delete_anyway: "Eliminar de todas formas",
      warning: "Advertencia",
      counter_of: "{n} de {total}",
    },
    safety: {
      header: "Reconocimiento de seguridad",
      clear: "Borrar",
      sign_to_check_in: "Firma para registrarte",
      wrong_house_title: "Podrías estar en la casa equivocada",
      wrong_house_body: "Estás a {distance} de {address}. Asegúrate de estar en la casa correcta antes de registrarte.",
    },
  },
  settings: {
    title: "Configuración",
    profile: {
      title: "Perfil",
      description: "Detalles de perfil",
      form: {
        name_label: "Nombre",
        organization_name_label: "Nombre de la organización",
        phone_number_label: "Número de teléfono",
      },
      success_message: "Información actualizada",
      delete_account: "Eliminar cuenta",
      refresh_profile: "Actualizar perfil",
    },
    preferences: {
      title: "Preferencias",
      description: "Actualiza las preferencias de notificaciones y seguimiento",
      notifications: "Notificaciones",
      location_services: "Servicios de ubicación",
      location_permission: {
        title: "Acceso a la ubicación",
        status_granted: "Permitido",
        status_denied: "No permitido",
        status_services_off: "La ubicación está desactivada en este teléfono",
        status_unknown: "Comprobando",
        explainer_title: "Tiempos de viaje en tu lista de trabajos",
        explainer_body:
          "Remi puede mostrar a qué distancia está cada trabajo. Esa distancia se calcula en este teléfono, y la ubicación que usa no se envía a Remi ni se muestra a nadie más.",
        not_shared_title: "No se comparte con la oficina",
        explainer_not_shared: "Permitir los tiempos de viaje no comparte tu ubicación con la oficina.",
        allow: "Permitir acceso a la ubicación",
        services_off_body:
          "La ubicación está desactivada en este teléfono, por lo que los tiempos de viaje no están disponibles. Puedes volver a activarla en los ajustes del teléfono.",
        settings_body: "El acceso a la ubicación para Remi se configura en los ajustes de tu teléfono.",
        open_settings: "Abrir ajustes",
      },
      save_images: "Guardar imágenes en el teléfono",
      on: "Prendido",
      off: "Apagado",
    },
    security: {
      title: "Seguridad",
      description: "Administra tus credenciales de inicio de sesión",
    },
  },
  maps: {
    cancel: "Cancelar",
    open_apple_maps: "Abrir Apple Maps",
    open_google_maps: "Abrir Google Maps",
    copy_address_to_clipboard: "Copiar al portapapeles",
  },
  notifications: {
    geofence_check_in: {
      with_homeowner:
        "Parece que estás en el sitio — te has registrado para el trabajo de techo de {homeownerFirstName}. Toca para ver los detalles del proyecto.",
      without_homeowner: "Parece que estás en el sitio — te has registrado. Toca para ver los detalles del proyecto.",
    },
    offline_sync: {
      pending_upload_singular: "1 elemento se subirá cuando vuelvas a estar en línea.",
      pending_upload_plural: "{count} elementos se subirán cuando vuelvas a estar en línea.",
    },
  },
  camera: {
    review_already_saved_accessibility_label: "Ya guardada en la lista de verificación",
    review_undo_delete_accessibility_label: "Conservar foto",
    review_delete_accessibility_label: "Eliminar foto",
    review_photos_accessibility_label: "Revisar fotos capturadas",
    min_photos_delete_warning: "Esta pregunta requiere al menos {count} foto(s).",
    focus_accessibility_label: "Toca para enfocar",
    not_ready: "Cámara no lista",
    not_ready_msg: "Por favor espera un momento e inténtalo de nuevo.",
    permission_microphone: "Necesitamos permiso de micrófono para grabar video.",
    unable_record_video: "No se puede grabar video",
    max_photos_reached: "Máximo de fotos alcanzado",
    max_photos_msg: "Ya has agregado el número máximo de fotos para esta pregunta.",
    question_locked_title: "Pregunta en uso",
    question_locked_msg: "Otro contratista está trabajando en esta pregunta en este momento.",
    unable_take_photo: "No se puede tomar la foto",
    try_again: "Por favor, inténtalo de nuevo.",
    photo_captured_announcement: "Foto capturada",
    take_photo_label: "Tomar foto",
    start_recording_label: "Iniciar grabación",
    stop_recording_label: "Detener grabación",
    video_limit_reached_title: "Grabación detenida",
    video_limit_reached_msg: "Los videos pueden durar hasta {seconds} segundos.",
    icloud_downloading: "Descargando de iCloud… {percent}%",
    permission_library: "Necesitamos permiso de la galería para seleccionar una imagen.",
    loading: "Cargando...",
    permission_required: "Permiso de cámara requerido",
    allow_camera: "Permitir cámara",
    close: "Cerrar",
    required: "Requerido",
    max: "Máx",
    current: "Actual",
    discard_photos_description_singular: "Hay {n} foto que no se añadirá a la lista de verificación.",
    discard_photos_description_plural: "Hay {n} fotos que no se añadirán a la lista de verificación.",
    discard_photos: "¿Descartar fotos?",
    nevermind: "No importa",
    discard: "Descartar",
    discarding: "Descartando...",
    upload_failed_title: "Error al subir",
    upload_failed_msg: "Algunas fotos no se pudieron subir. Por favor, inténtalo de nuevo.",
    save_to_camera_roll_title: "¿Guardar en el carrete?",
    save_to_camera_roll_msg:
      "Las fotos se añaden a la lista de verificación de todas formas. Puedes cambiar esto más tarde en Configuración > Preferencias.",
    save_to_camera_roll_decline: "No guardar",
    save_to_camera_roll_confirm: "Guardar",
    photo_mode: "FOTO",
    video_mode: "VIDEO",
    done: "Listo",
  },
  version_upgrade: {
    title: "Actualización Requerida",
    description:
      "Una nueva versión de la aplicación está disponible. Por favor actualiza para continuar usando la aplicación.",
    update_button: "Actualizar",
  },
} as const satisfies DeepStringLike<typeof mobileStringsEn>;
